"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { LogIn, LogOut, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiError, clearTabSession, requestJson } from "@/lib/client-api";
import { queryClient } from "@/lib/react-query";
import type { Brand, BrandStore, Session } from "./types";

const FORBIDDEN_MESSAGE =
  "이용 가능한 브랜드 또는 매장이 없습니다. AI 리뷰 관리 화면에 접근할 수 없습니다. 관리자에게 권한을 요청해 주세요.";
const CONNECTION_MESSAGE =
  "인증 서버에 연결하지 못했습니다. 로그인 상태를 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.";
const POJANGBOSS_LOGIN_URL =
  process.env.NEXT_PUBLIC_POJANGBOSS_LOGIN_URL ?? "https://stage-admin.pojangboss.com";
const SCOPE_RECOVERY_INTERVAL_MS = 3_000;
const STORE_OWNER_SELECTION_KEY = "owner";
// 로컬 개발 전용. 백엔드 DEV_LOGIN_ENABLED와 함께 켠다.
const DEV_LOGIN_ENABLED = process.env.NEXT_PUBLIC_DEV_LOGIN === "true";
const DEV_LOGIN_TIERS = [
  { tier: "staff", label: "스태프" },
  { tier: "hq_staff", label: "본사" },
  { tier: "store_owner", label: "점주" },
] as const;
type DevLoginTier = (typeof DEV_LOGIN_TIERS)[number]["tier"];

type AuthContextValue = {
  session: Session;
  selectedBrandId: number | null;
  selectedStoreId: number | null;
  availableStores: BrandStore[];
  selectedBrand: Brand | null;
  selectedStore: BrandStore | null;
  canChangeBrand: boolean;
  canChangeStore: boolean;
  canEditReplySettings: boolean;
  selectBrand: (brandId: number | null) => void;
  selectStore: (storeId: number | null) => void;
  logout: () => Promise<void>;
  scopeSearchParams: () => URLSearchParams;
};

const AuthContext = createContext<AuthContextValue | null>(null);
let bootstrapRequest: Promise<Session> | null = null;

function removeSsoToken() {
  const url = new URL(window.location.href);
  url.searchParams.delete("sso_token");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export function getParentReturnUrl(referrer: string): string | null {
  if (!referrer.trim()) return null;
  try {
    const url = new URL(referrer);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

async function loadSession() {
  const token = new URLSearchParams(window.location.search).get("sso_token");
  if (token) {
    removeSsoToken();
    await requestJson<Session>("/api/auth/sso", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sso_token: token }),
      notifyUnauthorized: false,
    });
  }
  return requestJson<Session>("/api/auth/session", { notifyUnauthorized: false });
}

function AccessMessage({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <main className="grid min-h-screen place-items-center bg-[var(--skeu-bg)] p-6">
      <section className="skeu-card w-full max-w-md p-8 text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-red-50 text-red-600">
          <ShieldAlert className="h-6 w-6" />
        </span>
        <h1 className="mt-4 text-xl font-bold text-[var(--foreground)]">접근할 수 없습니다</h1>
        <p className="mt-3 text-sm leading-6 text-[var(--muted-foreground)]">{message}</p>
        {onAction && (
          <Button className="mt-6" onClick={onAction}>
            {actionLabel ?? "확인"}
          </Button>
        )}
      </section>
    </main>
  );
}

function ReconnectPage({ onDevLogin }: { onDevLogin: (tier: DevLoginTier) => void }) {
  const isEmbedded = typeof window !== "undefined" && window.self !== window.top;
  const parentReturnUrl = isEmbedded ? getParentReturnUrl(document.referrer) : null;
  return (
    <main className="grid min-h-screen place-items-center bg-[var(--skeu-bg)] p-6">
      <section className="skeu-card w-full max-w-md p-8 text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-amber-50 text-amber-700">
          <ShieldAlert className="h-6 w-6" />
        </span>
        <h1 className="mt-4 text-xl font-bold text-[var(--foreground)]">로그인이 필요합니다</h1>
        <p className="mt-3 text-sm leading-6 text-[var(--muted-foreground)]">
          {isEmbedded
            ? "로그인이 끊겼습니다. 이어서 작업할 수 없습니다. 포장보스 화면을 새로고침한 뒤 AI 답글 관리를 다시 열어 주세요."
            : "로그인이 끊겼습니다. 리뷰 화면을 열 수 없습니다. 포장보스에서 다시 로그인한 뒤 들어와 주세요."}
        </p>
        {isEmbedded ? parentReturnUrl ? (
          <a
            href={parentReturnUrl}
            target="_top"
            className="mt-6 inline-flex h-9 w-full items-center justify-center rounded-sm border border-[var(--primary-600)] bg-[var(--primary-600)] px-3 text-sm font-medium text-white transition-colors hover:border-[var(--primary-400)] hover:bg-[var(--primary-400)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-300)] focus-visible:ring-offset-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            포장보스 화면 새로고침
          </a>
        ) : (
          <p className="mt-6 text-xs leading-5 text-[var(--muted-foreground)]">
            브라우저에서 포장보스 화면을 새로고침해 주세요.
          </p>
        ) : (
          <Button
            className="mt-6 w-full"
            onClick={() => window.location.assign(POJANGBOSS_LOGIN_URL)}
          >
            <LogIn className="mr-2 h-4 w-4" />
            포장보스 로그인
          </Button>
        )}
        {DEV_LOGIN_ENABLED && (
          <div className="mt-6 border-t border-dashed pt-4">
            <p className="text-xs text-[var(--muted-foreground)]">로컬 개발용 로그인</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {DEV_LOGIN_TIERS.map(({ tier, label }) => (
                <Button key={tier} variant="outline" size="sm" onClick={() => onDevLogin(tier)}>
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "reconnect" | "forbidden" | "error">("loading");
  const pathname = usePathname() || "/";
  const [brandSelection, setBrandSelection] = useState<number | null>(null);
  const [storeSelectionByPath, setStoreSelectionByPath] = useState<Record<string, number | null>>({});
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const lastScopeRecoveryAt = useRef(0);
  const selectionKey =
    session?.user.tier === "store_owner" ? STORE_OWNER_SELECTION_KEY : pathname;

  useEffect(() => {
    let active = true;
    bootstrapRequest ??= loadSession();
    void bootstrapRequest
      .then((nextSession) => {
        if (!active) return;
        if (nextSession.user.tier === "none" || nextSession.brands.length === 0) {
          setStatus("forbidden");
          return;
        }
        setSession(nextSession);
        setBrandSelection(nextSession.brands[0]?.id ?? null);
        if (nextSession.user.tier === "store_owner") {
          setStoreSelectionByPath({
            [STORE_OWNER_SELECTION_KEY]: nextSession.brands[0]?.brand_stores[0]?.id ?? null,
          });
        }
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (!active) return;
        setStatus(error instanceof ApiError && error.status === 401 ? "reconnect" : "error");
      });

    const handleUnauthorized = () => {
      bootstrapRequest = null;
      setSession(null);
      setStoreSelectionByPath({});
      setStatus("reconnect");
    };
    window.addEventListener("riu:unauthorized", handleUnauthorized);
    return () => {
      active = false;
      window.removeEventListener("riu:unauthorized", handleUnauthorized);
    };
  }, [bootstrapAttempt]);

  const retryBootstrap = useCallback(() => {
    bootstrapRequest = null;
    setStatus("loading");
    setBootstrapAttempt((attempt) => attempt + 1);
  }, []);

  const devLogin = useCallback(async (tier: DevLoginTier) => {
    await requestJson<Session>("/api/auth/dev-login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ tier }),
      notifyUnauthorized: false,
    }).catch(() => undefined);
    retryBootstrap();
  }, [retryBootstrap]);

  // 포장보스에서 접근 권한이 바뀌면 브라우저가 들고 있던 브랜드·매장 선택이 서버 세션과 어긋나 403이 발생한다.
  useEffect(() => {
    const recoverScope = () => {
      if (Date.now() - lastScopeRecoveryAt.current < SCOPE_RECOVERY_INTERVAL_MS) return;
      lastScopeRecoveryAt.current = Date.now();
      void requestJson<Session>("/api/auth/session", { notifyUnauthorized: false })
        .then((nextSession) => {
          bootstrapRequest = Promise.resolve(nextSession);
          setSession(nextSession);
          if (selectionKey !== STORE_OWNER_SELECTION_KEY) {
            setStoreSelectionByPath((current) => ({ ...current, [selectionKey]: null }));
          }
          void queryClient.invalidateQueries();
        })
        .catch(() => undefined);
    };

    window.addEventListener("riu:scope-stale", recoverScope);
    return () => window.removeEventListener("riu:scope-stale", recoverScope);
  }, [selectionKey]);

  // 포장보스에서 접근 권한이 바뀌면 브라우저가 들고 있던 선택이 세션과 어긋나므로 유효한 범위로 좁혀서 사용한다.
  const selectedBrandId = !session
    ? brandSelection
    : session.brands.some((brand) => brand.id === brandSelection)
      ? brandSelection
      : session.brands[0]?.id ?? null;

  const selectedBrand = useMemo(
    () => session?.brands.find((brand) => brand.id === selectedBrandId) ?? null,
    [session, selectedBrandId],
  );
  const availableStores = useMemo(() => {
    if (!session) return [];
    if (selectedBrand) return selectedBrand.brand_stores;
    return session.brands.flatMap((brand) => brand.brand_stores);
  }, [session, selectedBrand]);
  const storeSelection = storeSelectionByPath[selectionKey] ?? null;
  const selectedStoreId = availableStores.some((store) => store.id === storeSelection)
    ? storeSelection
    : null;
  const selectedStore =
    availableStores.find((store) => store.id === selectedStoreId) ?? null;

  // 전환 직전에 시작된 요청은 이전 브랜드·매장으로 보내지므로 취소해 403을 막는다.
  const selectBrand = useCallback((brandId: number | null) => {
    void queryClient.cancelQueries();
    setBrandSelection(brandId);
    setStoreSelectionByPath({});
  }, []);

  const selectStore = useCallback((storeId: number | null) => {
    void queryClient.cancelQueries();
    setStoreSelectionByPath((current) => ({ ...current, [selectionKey]: storeId }));
  }, [selectionKey]);

  const logout = useCallback(async () => {
    try {
      await requestJson<null>("/api/auth/logout", { method: "POST", notifyUnauthorized: false });
    } finally {
      clearTabSession();
      bootstrapRequest = null;
      setSession(null);
      setStoreSelectionByPath({});
      setStatus("reconnect");
    }
  }, []);

  const value = useMemo<AuthContextValue | null>(() => {
    if (!session) return null;
    return {
      session,
      selectedBrandId,
      selectedStoreId,
      availableStores,
      selectedBrand,
      selectedStore,
      canChangeBrand: session.user.tier === "staff" && session.brands.length > 1,
      canChangeStore: session.user.tier !== "store_owner" && availableStores.length > 1,
      // 답글 설정은 매장 화면에서만 다룬다.
      canEditReplySettings: session.user.tier === "store_owner",
      selectBrand,
      selectStore,
      logout,
      scopeSearchParams: () => {
        const params = new URLSearchParams();
        if (selectedBrandId !== null) params.set("brandId", String(selectedBrandId));
        if (selectedStoreId !== null) params.set("storeId", String(selectedStoreId));
        return params;
      },
    };
  }, [
    availableStores,
    logout,
    selectBrand,
    selectStore,
    selectedBrand,
    selectedBrandId,
    selectedStore,
    selectedStoreId,
    session,
  ]);

  if (status === "loading") {
    return <main className="grid min-h-screen place-items-center text-sm text-[var(--muted-foreground)]">로그인 확인 중...</main>;
  }
  if (status === "forbidden") return <AccessMessage message={FORBIDDEN_MESSAGE} />;
  if (status === "reconnect") return <ReconnectPage onDevLogin={devLogin} />;
  if (status === "error") {
    return (
      <AccessMessage
        message={CONNECTION_MESSAGE}
        actionLabel="다시 시도"
        onAction={retryBootstrap}
      />
    );
  }
  if (!value) return null;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("인증 컨텍스트가 없습니다.");
  return value;
}

export function LogoutButton() {
  const { logout } = useAuth();
  return (
    <button
      type="button"
      onClick={() => void logout()}
      className="rounded-lg p-2 text-[var(--muted-foreground)] hover:bg-white hover:text-[var(--foreground)]"
      aria-label="로그아웃"
    >
      <LogOut className="h-4 w-4" />
    </button>
  );
}
